import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabaseClient } from "@/lib/supabase";
import { useUpdateTemplate } from "@/features/templates/api/updateTemplate.ts";
import { useAuth } from "@/utils/auth";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Checkbox } from "@patient-watch/ui/checkbox";
import { Input } from "@patient-watch/ui/input";
import { Textarea } from "@patient-watch/ui/textarea";
import { Label } from "@patient-watch/ui/label";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { Button } from "@patient-watch/ui/button";
import { ArrowLeft, ClipboardList, Loader2, Pencil, Syringe } from "lucide-react";
import UnauthorizedPage from "@/components/pages/UnauthorizedPage";
import { Suspense, useEffect, useState } from "react";
import { SPECIALTIES } from "@patient-watch/types";
import TrackersPreview from "@/features/templates/components/TrackersPreview";
import EventsPreview from "@/features/templates/components/EventsPreview";
import type { TemplateSettingsSchemaT } from "@patient-watch/types";
import { Skeleton } from "@patient-watch/ui/skeleton";

const PublishTemplate = () => {
    const auth = useAuth();
    const { templateId } = Route.useParams();
    const navigate = useNavigate();
    const mutation = useUpdateTemplate();

    const { data: template, isLoading } = useQuery({
        queryKey: ["template", templateId],
        queryFn: async () => {
            const { data, error } = await supabaseClient
                .from("templates")
                .select("*")
                .eq("template_id", templateId)
                .single();

            if (error) throw error;
            return data;
        },
    });

    const [templateName, setTemplateName] = useState("");
    const [templateDescription, setTemplateDescription] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);

    // Set up breadcrumbs
    h.useNav([
        {
            label: "New Template",
            route: { to: "/templates" },
        },
        {
            label: template?.template_name || "Template",
            route: {
                to: "/templates/$templateId/publish",
                params: { templateId },
            },
        },
    ]);

    // Initialize state when template loads
    useEffect(() => {
        if (template) {
            setTemplateName(template.template_name || "");
            setTemplateDescription(template.template_description || "");
            setIsPublic(template.is_public ?? false);
            setSelectedSpecialties(template.specialties || []);
        }
    }, [template]);

    // Check admin access
    if (!auth?.roles?.isAdmin && !auth?.isSuperUser) {
        return <UnauthorizedPage message="Admin Only Page" />;
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    if (!template) {
        return <div>Template not found</div>;
    }

    const handleToggleSpecialty = (specialty: string) => {
        setSelectedSpecialties((prev) =>
            prev.includes(specialty) ? prev.filter((s) => s !== specialty) : [...prev, specialty]
        );
    };

    const handleSave = () => {
        mutation.mutate(
            {
                template_id: templateId,
                formData: {
                    template_name: templateName,
                    template_description: templateDescription,
                    is_public: isPublic,
                    specialties: selectedSpecialties,
                },
            },
            {
                onSuccess: () => {
                    navigate({ to: "/templates" });
                },
            }
        );
    };

    const handleEditTemplate = () => {
        if (template?.settings) {
            navigate({
                to: "/templates/$templateId/trackers",
                params: { templateId: template?.template_id || templateId },
                search: template?.settings as any,
            });
        }
    };

    return (
        <div className="space-y-6 mx-auto p-2 md:p-4 max-w-4xl">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/templates" })}>
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back to Templates
                </Button>
            </div>

            <div>
                <h1 className="font-semibold text-3xl tracking-tight">Publish Template</h1>
                <p className="text-muted-foreground">
                    Make this template publicly available and configure visibility settings
                </p>
            </div>

            <ui.Card>
                <ui.CardHeader>
                    <ui.CardTitle>Template Details</ui.CardTitle>
                    <ui.CardDescription>Basic information about this template</ui.CardDescription>
                </ui.CardHeader>
                <ui.CardContent className="space-y-4">
                    {/* Template Name */}
                    <div className="space-y-2">
                        <Label htmlFor="template_name">Template Name</Label>
                        <Input
                            id="template_name"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="e.g., Knee Replacement"
                        />
                    </div>

                    {/* Template Description */}
                    <div className="space-y-2">
                        <Label htmlFor="template_description">Description</Label>
                        <Textarea
                            id="template_description"
                            value={templateDescription}
                            onChange={(e) => setTemplateDescription(e.target.value)}
                            placeholder="Brief description of what this template tracks"
                            rows={3}
                        />
                    </div>

                    {/* Is Public Checkbox */}
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="is_public"
                            checked={isPublic}
                            onCheckedChange={(checked) => setIsPublic(checked === true)}
                        />
                        <Label htmlFor="is_public" className="font-normal text-sm">
                            Make this template publicly available to all clinicians
                        </Label>
                    </div>
                </ui.CardContent>
            </ui.Card>

            <ui.Card>
                <ui.CardHeader>
                    <ui.CardTitle>Specialties</ui.CardTitle>
                    <ui.CardDescription>Select which specialties should see this template</ui.CardDescription>
                </ui.CardHeader>
                <ui.CardContent>
                    <ui.ScrollArea className="border rounded-md h-[300px]">
                        <div className="gap-2 grid grid-cols-1 md:grid-cols-2 p-4">
                            {SPECIALTIES.map((specialty) => (
                                <div key={specialty} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`specialty-${specialty}`}
                                        checked={selectedSpecialties.includes(specialty)}
                                        onCheckedChange={() => handleToggleSpecialty(specialty)}
                                    />
                                    <Label
                                        htmlFor={`specialty-${specialty}`}
                                        className="font-normal text-sm cursor-pointer"
                                    >
                                        {specialty}
                                    </Label>
                                </div>
                            ))}
                        </div>
                        <ui.ScrollBar />
                    </ui.ScrollArea>
                    <p className="mt-2 text-muted-foreground text-xs">
                        {selectedSpecialties.length} {selectedSpecialties.length === 1 ? "specialty" : "specialties"}{" "}
                        selected
                    </p>
                </ui.CardContent>
            </ui.Card>

            <ui.Card>
                <ui.CardHeader>
                    <div className="flex justify-between items-center">
                        <div>
                            <ui.CardTitle>Template Preview</ui.CardTitle>
                            <ui.CardDescription>
                                Forms, schedules, and events configured in this template
                            </ui.CardDescription>
                        </div>
                        <Button size="sm" variant="outline" onClick={handleEditTemplate}>
                            <Pencil className="mr-2 w-4 h-4" />
                            Edit Template Settings
                        </Button>
                    </div>
                </ui.CardHeader>
                <ui.CardContent className="space-y-4">
                    {/* Trackers Preview */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <ClipboardList className="w-4 h-4 text-muted-foreground" />
                            <h4 className="font-medium text-sm">Forms & Schedules</h4>
                        </div>
                        <Suspense fallback={<Skeleton className="w-full h-24" />}>
                            <TrackersPreview template={template as any} />
                        </Suspense>
                    </div>

                    {/* Events Preview */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Syringe className="w-4 h-4 text-muted-foreground" />
                            <h4 className="font-medium text-sm">Required Events</h4>
                        </div>
                        <Suspense fallback={<Skeleton className="w-full h-16" />}>
                            <EventsPreview settings={template?.settings as TemplateSettingsSchemaT} />
                        </Suspense>
                    </div>
                </ui.CardContent>
            </ui.Card>

            {/* Action Buttons */}
            <div className="flex justify-between gap-4">
                <fc.CancelButton variant="outline" onClick={() => navigate({ to: "/templates" })} className="w-auto" />
                <fc.ConfirmButton
                    buttonType="button"
                    label="Publish Template"
                    isLoading={mutation.isPending}
                    onClick={handleSave}
                    className="w-auto"
                />
            </div>
        </div>
    );
};

export const Route = createFileRoute("/_app/templates/$templateId/publish")({
    component: PublishTemplate,
});
